import { PitchSlide, pitchSlides } from './slides';
import { DEFAULT_DESIGN_ID, getDesignOrDefault } from './designTemplates';

export interface PitchTemplateSection {
  key: string;
  tabLabel: string;
  guidance: string;
}

/** A ready-made pitch deck for a specific industry or business type. */
export interface PitchTemplate {
  id: string;
  name: string;
  /** Short industry label shown on the card, e.g. "EdTech". */
  industry: string;
  description: string;
  /** Visual design applied to the deck. */
  designId: string;
  /** Example slides investors would expect in this space. */
  sampleSlides: PitchSlide[];
  /** Slide outline used when generating a customized deck with AI. */
  sections: PitchTemplateSection[];
  /** Default prompt pre-filled on the create page; user can edit before generating. */
  samplePrompt: string;
  /** When true, omitted from template pickers but still usable by id (e.g. existing decks). */
  hidden?: boolean;
}

const cloneSlides = (slides: PitchSlide[]): PitchSlide[] => JSON.parse(JSON.stringify(slides));

const img = (keywords: string): string =>
  `https://loremflickr.com/800/600/${encodeURIComponent(keywords)}`;

export const pitchTemplates: PitchTemplate[] = [
  {
    id: 'edtech-africa',
    name: 'EdTech for Africa',
    industry: 'EdTech',
    hidden: true,
    description:
      'Interactive learning platform for students — syllabus-aligned courses, creator economy, and schools. Sample EdTech pitch deck.',
    designId: 'ocean-blue',
    sampleSlides: cloneSlides(pitchSlides),
    sections: [
      { key: 'intro', tabLabel: 'INTRO', guidance: 'Introduce the learning platform and mission.' },
      { key: 'problem', tabLabel: 'PROBLEM', guidance: 'Why passive or outdated learning fails students.' },
      { key: 'solution', tabLabel: 'SOLUTION', guidance: 'Interactive video courses and embedded animations.' },
      { key: 'validation', tabLabel: 'VALIDATION', guidance: 'Market demand and comparable successes in Africa.' },
      { key: 'market', tabLabel: 'MARKET', guidance: 'TAM/SAM for e-learning in target countries.' },
      { key: 'model', tabLabel: 'MODEL', guidance: 'Pricing for students, schools, and content creators.' },
      { key: 'edge', tabLabel: 'EDGE', guidance: 'Curriculum alignment and interactive differentiation.' },
      { key: 'gtm', tabLabel: 'GTM', guidance: 'Launch channels and growth targets.' },
      { key: 'team', tabLabel: 'TEAM', guidance: 'Founders and key hires.' },
      { key: 'traction', tabLabel: 'TRACTION', guidance: 'Live product, users, and milestones.' },
      { key: 'ask', tabLabel: 'ASK', guidance: 'Funding round, use of funds, and goals.' },
    ],
    samplePrompt:
      'An interactive e-learning platform for senior high school students in Ghana and Africa: syllabus-aligned video courses with embedded animations, a creator economy for educators, and tools for schools.',
  },
  {
    id: 'fintech-saas',
    name: 'Fintech SaaS',
    industry: 'Fintech',
    description:
      'Digital payments and lending infrastructure for SMEs — mobile wallets, credit scoring, and API-first banking.',
    designId: 'midnight-violet',
    sampleSlides: [
      {
        id: 'intro',
        title: 'Banking infrastructure for African SMEs',
        highlights: [
          'API-first payments and lending stack',
          'Built for mobile-first merchants',
          'Licensed partners in 3 markets',
          'Processing $12M monthly GMV in pilot',
        ],
        imageUrl: img('fintech,mobile,payment'),
        imageAlt: 'Person paying with mobile phone',
        tabLabel: 'INTRO',
      },
      {
        id: 'problem',
        title: 'SMEs are locked out of credit',
        highlights: [
          '80% of African SMEs lack formal credit history',
          'Legacy banks take weeks to onboard merchants',
          'Cash still dominates B2B supplier payments',
          'High fraud rates on informal lending',
        ],
        imageUrl: img('business,finance,problem'),
        imageAlt: 'Small business owner reviewing finances',
        tabLabel: 'PROBLEM',
      },
      {
        id: 'solution',
        title: 'One API for pay, lend, and reconcile',
        highlights: [
          'Instant merchant wallets and payouts',
          'Alternative credit scoring from transaction data',
          'Embedded lending for inventory and payroll',
          'Real-time reconciliation dashboard',
        ],
        imageUrl: img('dashboard,analytics,fintech'),
        imageAlt: 'Fintech dashboard on laptop',
        tabLabel: 'SOLUTION',
      },
      {
        id: 'market',
        title: '$48B SME fintech opportunity',
        highlights: [
          'Africa digital payments growing 20% CAGR',
          'TAM: $48B · SAM: $8B in East & West Africa',
          'Initial wedge: retail and agri-supply chains',
          'Regulatory sandboxes opening in Kenya & Nigeria',
        ],
        imageUrl: img('africa,business,growth'),
        imageAlt: 'Business growth chart',
        tabLabel: 'MARKET',
      },
      {
        id: 'model',
        title: 'Transaction + lending revenue',
        highlights: [
          '1.2% fee on payment volume',
          'Interest margin on embedded loans',
          'SaaS tier for enterprise reconciliation',
          '85% gross margin at scale',
        ],
        imageUrl: img('money,revenue,startup'),
        imageAlt: 'Revenue and business model',
        tabLabel: 'MODEL',
      },
      {
        id: 'ask',
        title: 'Raising $2M seed round',
        highlights: [
          'Expand to Nigeria and expand lending book',
          'Hire compliance, risk, and sales leads',
          'Target 50K merchants in 18 months',
          'Path to $100M annualized GMV',
        ],
        imageUrl: img('team,startup,meeting'),
        imageAlt: 'Startup team planning growth',
        tabLabel: 'ASK',
      },
    ],
    sections: [
      { key: 'intro', tabLabel: 'INTRO', guidance: 'Introduce the fintech product and wedge.' },
      { key: 'problem', tabLabel: 'PROBLEM', guidance: 'SME pain with payments, credit, and banking access.' },
      { key: 'solution', tabLabel: 'SOLUTION', guidance: 'Product capabilities: wallets, lending, APIs.' },
      { key: 'market', tabLabel: 'MARKET', guidance: 'Fintech TAM/SAM and target segment.' },
      { key: 'model', tabLabel: 'MODEL', guidance: 'Revenue from fees, interest, and SaaS.' },
      { key: 'ask', tabLabel: 'ASK', guidance: 'Funding ask and milestones.' },
    ],
    samplePrompt:
      'A B2B fintech providing payment APIs and embedded lending for small retailers and suppliers in Kenya and Nigeria, raising a seed round.',
  },
  {
    id: 'healthcare-medtech',
    name: 'Healthcare Platform',
    industry: 'Healthcare',
    description:
      'Telehealth and clinic management for emerging markets — virtual consults, e-prescriptions, and patient records.',
    designId: 'forest-emerald',
    sampleSlides: [
      {
        id: 'intro',
        title: 'Healthcare access for underserved communities',
        highlights: [
          'Telehealth + clinic OS in one platform',
          'Serving patients beyond major cities',
          'Partner network of 200+ clinicians',
          'Reducing wait times from weeks to hours',
        ],
        imageUrl: img('doctor,telehealth,patient'),
        imageAlt: 'Doctor on video call with patient',
        tabLabel: 'INTRO',
      },
      {
        id: 'problem',
        title: 'Care is far away and fragmented',
        highlights: [
          'Doctor-to-patient ratio 1:5,000 in rural areas',
          'Paper records lost between facilities',
          'Out-of-pocket costs drive care delays',
          'Specialists concentrated in capital cities',
        ],
        imageUrl: img('hospital,rural,healthcare'),
        imageAlt: 'Rural healthcare facility',
        tabLabel: 'PROBLEM',
      },
      {
        id: 'solution',
        title: 'Virtual care with local follow-through',
        highlights: [
          'Video consults with verified specialists',
          'Digital prescriptions and lab referrals',
          'Clinic dashboard for nurses and GPs',
          'Insurance and mobile-money billing',
        ],
        imageUrl: img('medical,tablet,clinic'),
        imageAlt: 'Clinician using tablet in clinic',
        tabLabel: 'SOLUTION',
      },
      {
        id: 'validation',
        title: 'Patients and clinics already onboard',
        highlights: [
          '18K monthly active patients',
          '40 partner clinics across 2 regions',
          'Net promoter score of 62',
          'Pilot with regional health insurer',
        ],
        imageUrl: img('healthcare,team,clinic'),
        imageAlt: 'Healthcare team in clinic',
        tabLabel: 'VALIDATION',
      },
      {
        id: 'model',
        title: 'Per-visit and SaaS subscriptions',
        highlights: [
          '$4–8 per telehealth consultation',
          'Clinic SaaS from $150/month',
          'Insurer partnerships for capitated care',
          'Pharmacy referral commissions',
        ],
        imageUrl: img('health,insurance,payment'),
        imageAlt: 'Healthcare payment and insurance',
        tabLabel: 'MODEL',
      },
      {
        id: 'ask',
        title: 'Raising $1.5M to scale clinics',
        highlights: [
          'Expand to 150 clinics in 12 months',
          'Build specialist marketplace',
          'Regulatory approvals in second country',
          'Break-even at 35K visits/month',
        ],
        imageUrl: img('medical,startup,growth'),
        imageAlt: 'Healthcare startup growth planning',
        tabLabel: 'ASK',
      },
    ],
    sections: [
      { key: 'intro', tabLabel: 'INTRO', guidance: 'Healthcare platform mission and reach.' },
      { key: 'problem', tabLabel: 'PROBLEM', guidance: 'Access gaps, fragmentation, and cost barriers.' },
      { key: 'solution', tabLabel: 'SOLUTION', guidance: 'Telehealth, records, and clinic tools.' },
      { key: 'validation', tabLabel: 'VALIDATION', guidance: 'Traction with patients, clinics, and partners.' },
      { key: 'model', tabLabel: 'MODEL', guidance: 'Visit fees, SaaS, and insurer revenue.' },
      { key: 'ask', tabLabel: 'ASK', guidance: 'Funding and scale milestones.' },
    ],
    samplePrompt:
      'A telehealth and clinic management platform connecting rural patients to specialists in East Africa, with mobile-money billing and insurer partnerships.',
  },
  {
    id: 'ecommerce-d2c',
    name: 'D2C Consumer Brand',
    industry: 'E-commerce',
    description:
      'Direct-to-consumer brand pitch — hero product, unit economics, social acquisition, and omnichannel expansion.',
    designId: 'sunset-coral',
    sampleSlides: [
      {
        id: 'intro',
        title: 'Skincare built for melanin-rich skin',
        highlights: [
          'D2C brand with 40K repeat customers',
          'Formulated by dermatologists in Lagos',
          'Sold online and in 120 retail doors',
          'Community-led product development',
        ],
        imageUrl: img('skincare,beauty,product'),
        imageAlt: 'Skincare products on display',
        tabLabel: 'INTRO',
      },
      {
        id: 'problem',
        title: 'Global brands miss local skin needs',
        highlights: [
          'Mass products ignore hyperpigmentation concerns',
          'Import prices put quality out of reach',
          'Counterfeit products erode trust',
          'Limited education at point of sale',
        ],
        imageUrl: img('beauty,shopping,consumer'),
        imageAlt: 'Consumer browsing beauty products',
        tabLabel: 'PROBLEM',
      },
      {
        id: 'solution',
        title: 'Affordable, clinically tested routines',
        highlights: [
          '3-step systems under $25 retail',
          'Ingredients sourced and tested locally',
          'Subscription refills drive retention',
          'Shade-inclusive marketing and creators',
        ],
        imageUrl: img('cosmetics,brand,studio'),
        imageAlt: 'Brand product photography',
        tabLabel: 'SOLUTION',
      },
      {
        id: 'traction',
        title: 'Strong unit economics at scale',
        highlights: [
          '$1.8M ARR · 62% gross margin',
          'CAC payback in 2.1 months on Meta/TikTok',
          '38% repeat purchase rate within 90 days',
          'Wholesale growing 15% quarter-over-quarter',
        ],
        imageUrl: img('ecommerce,sales,growth'),
        imageAlt: 'E-commerce sales growth',
        tabLabel: 'TRACTION',
      },
      {
        id: 'gtm',
        title: 'Social-first, retail-backed GTM',
        highlights: [
          'Creator partnerships in 5 markets',
          'Pop-up retail in malls and campuses',
          'WhatsApp reorder and support channel',
          'Launching in Kenya Q3 via distributor',
        ],
        imageUrl: img('social,marketing,retail'),
        imageAlt: 'Social media and retail marketing',
        tabLabel: 'GTM',
      },
      {
        id: 'ask',
        title: 'Raising $800K to expand retail',
        highlights: [
          'Triple retail footprint to 350 doors',
          'Launch two new product lines',
          'Build in-house content studio',
          'Target $4M ARR in 18 months',
        ],
        imageUrl: img('retail,brand,expansion'),
        imageAlt: 'Retail brand expansion',
        tabLabel: 'ASK',
      },
    ],
    sections: [
      { key: 'intro', tabLabel: 'INTRO', guidance: 'Brand story and hero product.' },
      { key: 'problem', tabLabel: 'PROBLEM', guidance: 'Gap in the market for target consumer.' },
      { key: 'solution', tabLabel: 'SOLUTION', guidance: 'Product, pricing, and differentiation.' },
      { key: 'traction', tabLabel: 'TRACTION', guidance: 'Revenue, margins, and retention metrics.' },
      { key: 'gtm', tabLabel: 'GTM', guidance: 'Acquisition channels and expansion plan.' },
      { key: 'ask', tabLabel: 'ASK', guidance: 'Funding use and revenue targets.' },
    ],
    samplePrompt:
      'A direct-to-consumer skincare brand for melanin-rich skin, sold online and in retail across West Africa, with strong social acquisition and repeat purchase rates.',
  },
  {
    id: 'clean-energy',
    name: 'Clean Energy',
    industry: 'Climate',
    description:
      'Solar and storage for households and agribusiness — pay-as-you-go hardware, remote monitoring, and carbon impact.',
    designId: 'forest-emerald',
    sampleSlides: [
      {
        id: 'intro',
        title: 'Solar power for smallholder farmers',
        highlights: [
          'Pay-as-you-go solar irrigation kits',
          'Remote monitoring via mobile app',
          'Deployed across 1,200 farms in Kenya',
          'Cutting diesel costs by up to 70%',
        ],
        imageUrl: img('solar,farm,africa'),
        imageAlt: 'Solar panels on a farm',
        tabLabel: 'INTRO',
      },
      {
        id: 'problem',
        title: 'Energy costs limit farm yields',
        highlights: [
          'Irrigation depends on expensive diesel pumps',
          'Grid power unreliable in rural counties',
          'Upfront solar costs block smallholders',
          'Water waste from manual scheduling',
        ],
        imageUrl: img('farmer,irrigation,field'),
        imageAlt: 'Farmer irrigating crops',
        tabLabel: 'PROBLEM',
      },
      {
        id: 'solution',
        title: 'Solar kits with smart pumping',
        highlights: [
          'Modular panels + pump + storage',
          'Pay weekly via mobile money',
          'App-controlled schedules and alerts',
          'Local technicians for install and service',
        ],
        imageUrl: img('solar,technology,agriculture'),
        imageAlt: 'Solar irrigation technology',
        tabLabel: 'SOLUTION',
      },
      {
        id: 'impact',
        title: 'Measurable climate and income impact',
        highlights: [
          '2.4K tCO₂e avoided annually per 100 kits',
          'Average 35% yield increase reported',
          'Farmers save $40/month on fuel',
          'Aligned with SDG 7 and SDG 2 goals',
        ],
        imageUrl: img('green,energy,climate'),
        imageAlt: 'Clean energy and climate impact',
        tabLabel: 'IMPACT',
      },
      {
        id: 'model',
        title: 'Hardware + recurring service fees',
        highlights: [
          '$35/month subscription over 36 months',
          'Hardware COGS recovered by month 14',
          'Service and monitoring fee margin 45%',
          'Carbon credit revenue pilot underway',
        ],
        imageUrl: img('renewable,business,model'),
        imageAlt: 'Renewable energy business model',
        tabLabel: 'MODEL',
      },
      {
        id: 'ask',
        title: 'Raising $3M Series A',
        highlights: [
          'Finance 5,000 additional kits',
          'Expand technician network to Uganda',
          'Integrate carbon credit marketplace',
          'Reach cash-flow positive in 24 months',
        ],
        imageUrl: img('solar,team,startup'),
        imageAlt: 'Clean energy startup team',
        tabLabel: 'ASK',
      },
    ],
    sections: [
      { key: 'intro', tabLabel: 'INTRO', guidance: 'Clean energy product and customer.' },
      { key: 'problem', tabLabel: 'PROBLEM', guidance: 'Energy access and cost pain points.' },
      { key: 'solution', tabLabel: 'SOLUTION', guidance: 'Hardware, financing, and monitoring.' },
      { key: 'impact', tabLabel: 'IMPACT', guidance: 'Climate, yield, and income outcomes.' },
      { key: 'model', tabLabel: 'MODEL', guidance: 'Subscription, margins, and carbon credits.' },
      { key: 'ask', tabLabel: 'ASK', guidance: 'Funding round and deployment targets.' },
    ],
    samplePrompt:
      'A pay-as-you-go solar irrigation company serving smallholder farmers in Kenya and Uganda, reducing diesel use and increasing crop yields.',
  },
  {
    id: 'b2b-saas',
    name: 'B2B SaaS',
    industry: 'SaaS',
    description:
      'Workflow automation for mid-market teams — integrations, seat-based pricing, and land-and-expand sales motion.',
    designId: 'paper-ink',
    sampleSlides: [
      {
        id: 'intro',
        title: 'Operations hub for growing teams',
        highlights: [
          'Workflow automation for 50–500 person companies',
          'Integrates Slack, Google Workspace, and ERPs',
          'Customers save 12 hrs/week per team lead',
          'SOC 2 Type II certified',
        ],
        imageUrl: img('saas,office,teamwork'),
        imageAlt: 'Team collaborating with software',
        tabLabel: 'INTRO',
      },
      {
        id: 'problem',
        title: 'Ops teams drown in manual work',
        highlights: [
          'Approvals scattered across email and chat',
          'No single source of truth for processes',
          'Enterprise tools too heavy and expensive',
          'Spreadsheet workflows break at scale',
        ],
        imageUrl: img('office,workflow,stress'),
        imageAlt: 'Overwhelmed operations team',
        tabLabel: 'PROBLEM',
      },
      {
        id: 'solution',
        title: 'Automate approvals in minutes',
        highlights: [
          'Drag-and-drop workflow builder',
          'Pre-built templates for finance and HR',
          'Audit trails and role-based access',
          'API and webhook extensibility',
        ],
        imageUrl: img('software,dashboard,workflow'),
        imageAlt: 'Workflow automation dashboard',
        tabLabel: 'SOLUTION',
      },
      {
        id: 'traction',
        title: 'Land-and-expand momentum',
        highlights: [
          '$620K ARR · 128% net revenue retention',
          '85 paying companies · avg 28 seats',
          'Pilot-to-paid conversion at 41%',
          'Top 10 customers expanded 2.3x in year one',
        ],
        imageUrl: img('startup,metrics,saas'),
        imageAlt: 'SaaS growth metrics',
        tabLabel: 'TRACTION',
      },
      {
        id: 'gtm',
        title: 'Product-led with inside sales',
        highlights: [
          'Free tier for teams under 10 users',
          'Inbound from template marketplace',
          'Inside sales for 50+ seat deals',
          'Partner channel with regional consultancies',
        ],
        imageUrl: img('sales,software,meeting'),
        imageAlt: 'SaaS sales and GTM',
        tabLabel: 'GTM',
      },
      {
        id: 'ask',
        title: 'Raising $4M Series A',
        highlights: [
          'Scale sales and customer success teams',
          'Launch enterprise SSO and advanced analytics',
          'Enter EU market via UK entity',
          'Target $3M ARR within 18 months',
        ],
        imageUrl: img('startup,presentation,saas'),
        imageAlt: 'SaaS startup fundraising',
        tabLabel: 'ASK',
      },
    ],
    sections: [
      { key: 'intro', tabLabel: 'INTRO', guidance: 'SaaS product and target customer size.' },
      { key: 'problem', tabLabel: 'PROBLEM', guidance: 'Manual ops pain and tool gaps.' },
      { key: 'solution', tabLabel: 'SOLUTION', guidance: 'Automation features and integrations.' },
      { key: 'traction', tabLabel: 'TRACTION', guidance: 'ARR, retention, and expansion metrics.' },
      { key: 'gtm', tabLabel: 'GTM', guidance: 'PLG, sales motion, and channels.' },
      { key: 'ask', tabLabel: 'ASK', guidance: 'Round size and growth targets.' },
    ],
    samplePrompt:
      'A B2B SaaS workflow automation tool for mid-market operations teams, with integrations to Slack and Google Workspace and strong net revenue retention.',
  },
];

export const visiblePitchTemplates = pitchTemplates.filter((template) => !template.hidden);

export const DEFAULT_PITCH_TEMPLATE_ID = visiblePitchTemplates[0].id;

export const getPitchTemplate = (id: string): PitchTemplate | undefined =>
  pitchTemplates.find((template) => template.id === id);

export const getPitchTemplateOrDefault = (id?: string | null): PitchTemplate =>
  (id ? getPitchTemplate(id) : undefined) ?? visiblePitchTemplates[0];

/** Palette for rendering a template card preview. */
export const getTemplatePalette = (template: PitchTemplate) =>
  getDesignOrDefault(template.designId).palette;
